import { nonNullable } from '@skippay/core/utils/nonNullable';

import type { UpsellServiceAvailableOffer } from '@types/upsellServiceAvailableOffer';

import { jarabotDevStore } from '../devStore';
import { JARABOT_EVENTS } from '../jarabotClient';
import { readInsuranceOfferMemory, saveInsuranceOfferContact } from './memory';
import { createInsuranceOfferPayload } from './payload';
import { selectInsuranceContact } from './selection';
import type { JarabotLoginOfferEvaluator } from '../loginOffers/types';
import MyUpsellServicesAvailableOffersQuery from '../queries/MyUpsellServicesAvailableOffersQuery.graphql';

// The offer is evaluated app-wide rather than from a single screen, so it reports its own surface.
const SCREEN_NAME = 'insurance_offer';

type MyUpsellServicesAvailableOffersData = {
	myUpsellServicesAvailableOffers: Array<UpsellServiceAvailableOffer | null> | null;
};

export const evaluateInsuranceOffer: JarabotLoginOfferEvaluator = async ({
	client,
	customerId,
	selfCareUrl,
	now,
}) => {
	const { data } = await client.query<MyUpsellServicesAvailableOffersData>({
		query: MyUpsellServicesAvailableOffersQuery,
		fetchPolicy: 'no-cache',
	});

	const offers = (data?.myUpsellServicesAvailableOffers ?? []).filter(nonNullable);
	const memory = readInsuranceOfferMemory(customerId, now);
	jarabotDevStore.trackInsuranceMemory(memory);

	const selection = selectInsuranceContact(offers, memory, now);

	if (!selection) return null;

	const offerIds = selection.purchases.map((purchase) => purchase.id);

	return {
		event: JARABOT_EVENTS.CUSTOMER_INSURANCE_OFFER,
		screenName: SCREEN_NAME,
		payload: createInsuranceOfferPayload({ selection, selfCareUrl }),
		onSent: () => {
			const saved = saveInsuranceOfferContact({
				memory,
				customerId,
				offerIds,
				contactType: selection.contactType,
				now,
			});

			jarabotDevStore.trackInsuranceSent(selection.contactType, offerIds);
			jarabotDevStore.trackInsuranceMemory(saved);
		},
	};
};
